import dayjs from "dayjs";
import { isNil, omitBy } from "lodash-es";
import axiosInstance, { type ApiResponse, useGet } from "@/api";
import type { IDeliveryOrder } from "@/pages/Inventory/delivery/model";
import { withDraftCountRefresh } from "@/stores/domains/inventory/inventoryStore";
import { DATE_FORMATS } from "@/utils/date";
import { buildClinicUrl } from "@/utils/serviceHelpers";

export interface IDeliveryOrderBody {
  deliveryOrderNumber: string;
  invoiceNumber: string;
  purchaseOrderNumber: string;
  transactionType: string;
  supplierId: number;
  deliveryDate: string;
  deliveryOrderStatus: string;
  subTotalBeforeTax: number;
  gstRate: number;
  total: number;
  deliveryOrderItems: Array<{
    chargeCode: string;
    itemName: string;
    quantity: number;
    unit: string;
    pricePerUnit: number;
    amount: number;
    batchNumber: string;
    expiryDate: string;
    version?: number;
  }>;
  version?: number;
}

export interface IDeliveryOrderParams {
  skip?: number;
  limit?: number;
  search?: string;
  transactionType?: string[];
  deliveryOrderStatus?: string[];
  deliveryDateStart?: string;
  deliveryDateEnd?: string;
}

const deliveryOrderService = {
  createDeliveryOrder: async (deliveryOrder: IDeliveryOrderBody) => {
    return withDraftCountRefresh(async () => {
      const cleanDeliveryOrder = omitBy(deliveryOrder, (value) => isNil(value));
      const response = await axiosInstance.post(
        buildClinicUrl("/delivery-orders"),
        cleanDeliveryOrder,
      );
      return response.data;
    });
  },
  getDeliveryOrderById: async (id: string): Promise<ApiResponse<IDeliveryOrder>> => {
    const response = await axiosInstance.get(buildClinicUrl(`/delivery-orders/${id}`));
    return response.data;
  },
  useDeliveryOrderById: (id: string) => {
    const url = buildClinicUrl(`/delivery-orders/${id}`);
    return useGet<IDeliveryOrder>(url);
  },
  updateDeliveryOrder: async (id: string, deliveryOrder: IDeliveryOrderBody) => {
    const cleanDeliveryOrder = omitBy(deliveryOrder, (value) => isNil(value));
    const response = await axiosInstance.put(
      buildClinicUrl(`/delivery-orders/${id}`),
      cleanDeliveryOrder,
    );
    return response.data;
  },
  deleteDeliveryOrder: async (id: string, version: number) => {
    return withDraftCountRefresh(async () => {
      const response = await axiosInstance.delete(buildClinicUrl(`/delivery-orders/${id}`), {
        data: {
          version,
        },
      });
      return response.data;
    });
  },

  getDraftDeliveryOrdersCount: async (): Promise<ApiResponse<number>> => {
    const response = await axiosInstance.get(buildClinicUrl("/delivery-orders/draft/count"));
    return response.data;
  },

  finalizeDeliveryOrder: async (id: string, version: number) => {
    return withDraftCountRefresh(async () => {
      const response = await axiosInstance.patch(
        buildClinicUrl(`/delivery-orders/${id}/finalize`),
        {
          version,
        },
      );
      return response.data;
    });
  },

  getDeliveryOrders: async (
    params: IDeliveryOrderParams,
  ): Promise<ApiResponse<IDeliveryOrder[]>> => {
    if (params.deliveryDateStart) {
      params.deliveryDateStart = dayjs(params.deliveryDateStart, DATE_FORMATS.SHORT_DATE_V2).format(
        "YYYY-MM-DD",
      );
    } else {
      delete params.deliveryDateStart;
    }
    if (params.deliveryDateEnd) {
      params.deliveryDateEnd = dayjs(params.deliveryDateEnd, DATE_FORMATS.SHORT_DATE_V2).format(
        "YYYY-MM-DD",
      );
    } else {
      delete params.deliveryDateEnd;
    }
    const cleanParams = omitBy({ ...params }, (value) => isNil(value));
    const response = await axiosInstance.get(buildClinicUrl("/delivery-orders"), {
      params: cleanParams,
      paramsSerializer: {
        indexes: null,
      },
    });
    return response.data;
  },
};

export default deliveryOrderService;
